/* Zailarah - reels strip + full-screen viewer + shoppable product panel (<zl-reels>, section zl-reels).
   Behaviour ported from the product page prototype (lines 2164-2339; the collection page copy 1420-1596 is identical):
   strip arrows, muted previews while >= 60% visible, viewer with progress bars / tap zones / desktop arrows / keys /
   swipe + press-and-hold, auto-advance (video end or still timer), mute toggle, shoppable panel (idle / pick / added).
   Shopify wiring: the strip and the per-reel data are rendered server-side by the section (real products, prices
   through zl-promo, money in the visitor's currency); adding goes through `await ZL.cart.add()`.
   Placement: with placement "slot" the strip moves into the first free [data-zl-reels-slot] of the page (product info
   column / collection grid), otherwise it stays where the section is.
   Public API: window.ZL.reels = { mount(), mountInto(slotEl), destroy(), instances() } (alias window.ZLReels).
   Events emitted on document: zl:reels:open, zl:reels:close, zl:reels:mounted.
   Listens for: zl:collection:rendered (collection grid re-rendered -> re-mount, optional detail.collection
   { handle, title } re-orders the reels and updates the subtitle), shopify:section:load (another section re-rendered). */
(function () {
  'use strict';

  var win = window;
  var doc = document;
  var ZL = (win.ZL = win.ZL || {});
  var instances = [];
  var uid = 0;

  var ICON_PREV = '<svg viewBox="0 0 12 12"><path d="M7.5 2 3.5 6l4 4" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>';
  var ICON_NEXT = '<svg viewBox="0 0 12 12"><path d="m4.5 2 4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>';
  var ICON_CLOSE = '<svg viewBox="0 0 24 24"><path d="M5 5l14 14M19 5 5 19"/></svg>';
  var SPK_OFF = '<svg viewBox="0 0 24 24"><path d="M4 9h4l5-4v14l-5-4H4z"/><path d="m17 9 4 6m0-6-4 6"/></svg>';
  var SPK_ON = '<svg viewBox="0 0 24 24"><path d="M4 9h4l5-4v14l-5-4H4z"/><path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12"/></svg>';
  var ICON_CHECK = '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="m7.5 12.5 3 3 6-6.5"/></svg>';

  var HOLD_MS = 260;
  var TICK_MS = 40;

  function esc(s) {
    if (typeof ZL.esc === 'function') return ZL.esc(s);
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function str(value, fallback) {
    return value != null && String(value).trim() !== '' ? String(value) : (fallback || '');
  }
  function reducedMotion() {
    return typeof ZL.prefersReducedMotion === 'function' ? ZL.prefersReducedMotion() : false;
  }
  function playSafe(video) {
    try {
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
    } catch (e) { /* autoplay refused */ }
  }

  /* first free [data-zl-reels-slot] for this instance (a slot is "taken" once another reels section filled it) */
  function findFreeSlot(owner) {
    var slots = doc.querySelectorAll('[data-zl-reels-slot]');
    for (var i = 0; i < slots.length; i++) {
      var taken = slots[i].getAttribute('data-zl-reels-filled');
      if (!taken || taken === owner.sectionId) return slots[i];
    }
    return null;
  }

  class ZlReels extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.root = this.querySelector('[data-zl-reels-root]');
      this.host = this.querySelector('[data-zl-reels-host]');
      var dataEl = this.querySelector('script[data-zl-reels-data]');
      if (!this.root || !this.host || !dataEl) return;
      var data;
      try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
      if (!data || !data.items || !data.items.length) return;
      this._ready = true;
      this.uid = ++uid;
      this.data = data;
      this.items = data.items;
      this.labels = data.labels || {};
      this.sectionId = this.dataset.sectionId || String(this.uid);
      this.placement = this.dataset.placement === 'inline' ? 'inline' : 'slot';
      this.stillMs = Number(this.dataset.stillMs) || 5000;
      this.autoplay = this.dataset.autoplay !== '0';
      this.showShop = this.dataset.showShop !== '0';
      this.muted = this.dataset.muted !== '0';
      this.lockId = 'zl-reels-' + this.sectionId;
      this.track = this.root.querySelector('.zlr__track');
      this.viewer = null;
      this.viewerOpen = false;
      this.currentSlot = null;
      this.wasSlotted = false;

      this._onRootClick = this.onRootClick.bind(this);
      this._onBlockSelect = this.onBlockSelect.bind(this);
      this._onCollection = this.onCollectionRendered.bind(this);
      this._onSectionLoad = this.onSectionLoad.bind(this);
      this._onKey = this.onKey.bind(this);

      this.root.addEventListener('click', this._onRootClick);
      this.root.addEventListener('shopify:block:select', this._onBlockSelect);
      doc.addEventListener('zl:collection:rendered', this._onCollection);
      doc.addEventListener('shopify:section:load', this._onSectionLoad);

      this.observePreviews();
      instances.push(this);
      this.mount();
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      this.close(true);
      if (this.io) { this.io.disconnect(); this.io = null; }
      this.root.removeEventListener('click', this._onRootClick);
      this.root.removeEventListener('shopify:block:select', this._onBlockSelect);
      doc.removeEventListener('zl:collection:rendered', this._onCollection);
      doc.removeEventListener('shopify:section:load', this._onSectionLoad);
      this.removeViewer();
      this.goHome();
      var at = instances.indexOf(this);
      if (at > -1) instances.splice(at, 1);
    }

    /* ------------------------------------------------------------------ placement */

    mount() {
      if (!this._ready) return;
      if (this.placement === 'slot') {
        var slot = this.currentSlot && this.currentSlot.isConnected && this.currentSlot.contains(this.root) ? this.currentSlot : findFreeSlot(this);
        if (slot) { this.mountInto(slot); return; }
        if (this.wasSlotted) {
          /* the host context (e.g. an emptied collection grid) no longer offers a slot: like the prototype, no reels */
          this.close(true);
          this.releaseSlot();
          if (this.root.parentNode) this.root.parentNode.removeChild(this.root);
          this.host.hidden = true;
          this.setAttribute('data-mounted', '');
          return;
        }
      }
      this.goHome();
      this.setAttribute('data-mounted', '');
    }

    mountInto(slot) {
      if (!this._ready || !slot) return;
      if (this.currentSlot && this.currentSlot !== slot) this.releaseSlot();
      if (this.root.parentNode !== slot) slot.appendChild(this.root);
      slot.setAttribute('data-zl-reels-filled', this.sectionId);
      this.currentSlot = slot;
      this.wasSlotted = true;
      this.host.hidden = true;
      this.setAttribute('data-mounted', '');
      if (typeof ZL.emit === 'function') ZL.emit('zl:reels:mounted', { sectionId: this.sectionId, slot: slot, root: this.root });
    }

    releaseSlot() {
      if (this.currentSlot && this.currentSlot.getAttribute('data-zl-reels-filled') === this.sectionId) {
        this.currentSlot.removeAttribute('data-zl-reels-filled');
      }
      this.currentSlot = null;
    }

    /* back to where the section rendered it (in place) */
    goHome() {
      this.releaseSlot();
      if (this.root.parentNode !== this.host) this.host.appendChild(this.root);
      this.host.hidden = false;
    }

    /* ZL.reels.destroy(): close + take the strip off the page (mount() puts it back) */
    unmount() {
      if (!this._ready) return;
      this.close(true);
      this.removeViewer();
      this.releaseSlot();
      if (this.root.parentNode) this.root.parentNode.removeChild(this.root);
      this.host.hidden = true;
    }

    onSectionLoad(event) {
      /* our own section reload re-creates this element; any other section (e.g. main-product) may bring a new slot */
      if (this.contains(event.target)) return;
      this.mount();
    }

    onCollectionRendered(event) {
      var detail = (event && event.detail) || {};
      var c = detail.collection || null;
      var handle = (c && c.handle) || detail.collectionHandle || '';
      var title = (c && c.title) || detail.collectionTitle || '';
      if (handle) this.setCollection(handle, title);
      this.mount();
    }

    /* collection switched without a page load: subtitle + "this collection first" order (prototype setReels) */
    setCollection(handle, title) {
      if (handle === this.dataset.collection) return;
      this.dataset.collection = handle;
      var sub = this.root.querySelector('[data-zl-reels-sub]');
      if (sub && this.data.subtitle) {
        var name = handle === 'all' || !title ? str(this.data.allLabel) : title;
        sub.textContent = this.data.subtitle.replace(/\[collection\]/g, name);
      }
      if (this.dataset.prioritise === '0' || !this.track) return;
      var cards = Array.prototype.slice.call(this.track.querySelectorAll('.zlr__card'));
      if (cards.length !== this.items.length) return;
      var pairs = this.items.map(function (item, i) { return { item: item, card: cards[i] }; });
      var inC = function (pair) {
        var p = pair.item.product;
        return Boolean(p && (handle === 'all' || (p.collections || []).indexOf(handle) > -1));
      };
      var ordered = pairs.filter(inC).concat(pairs.filter(function (pair) { return !inC(pair); }));
      var self = this;
      this.close(true);
      this.removeViewer();
      this.items = ordered.map(function (pair) { return pair.item; });
      ordered.forEach(function (pair, i) {
        pair.card.setAttribute('data-i', String(i));
        var label = str(self.labels.play, 'Play reel') + ' ' + (i + 1);
        if (pair.item.title) label += ': ' + pair.item.title;
        pair.card.setAttribute('aria-label', label);
        self.track.appendChild(pair.card);
      });
      this.track.scrollLeft = 0;
    }

    /* ------------------------------------------------------------------ strip */

    onRootClick(event) {
      var arrow = event.target.closest('.zlr__arrows [data-d]');
      if (arrow && this.root.contains(arrow)) {
        if (this.track) this.track.scrollBy({ left: Number(arrow.getAttribute('data-d')) * this.track.clientWidth * 0.66, behavior: 'smooth' });
        return;
      }
      var card = event.target.closest('.zlr__card');
      if (card && this.root.contains(card)) this.open(Number(card.getAttribute('data-i')) || 0);
    }

    observePreviews() {
      if (!this.autoplay || !('IntersectionObserver' in win) || !this.track) return;
      var self = this;
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target.querySelector('video');
          if (!video || self.viewerOpen) return;
          if (entry.isIntersecting && !reducedMotion()) playSafe(video);
          else video.pause();
        });
      }, { threshold: 0.6 });
      this.track.querySelectorAll('.zlr__card').forEach(function (card) { self.io.observe(card); });
    }

    /* theme editor: show the selected reel in the strip (never opens the viewer) */
    onBlockSelect(event) {
      var card = event.target && event.target.closest ? event.target.closest('.zlr__card') : null;
      if (!card || !this.track) return;
      var offset = card.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
      this.track.scrollBy({ left: offset, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    /* ------------------------------------------------------------------ viewer */

    buildViewer() {
      if (this.viewer) return;
      var L = this.labels;
      var v = doc.createElement('div');
      v.className = 'zlr-v';
      v.setAttribute('role', 'dialog');
      v.setAttribute('aria-modal', 'true');
      v.setAttribute('aria-label', str(this.data.title));
      v.setAttribute('aria-hidden', 'true');
      v.innerHTML = '<div class="zlr-v__stage" data-stage>' +
          '<div class="zlr-v__bars">' + this.items.map(function (_, i) { return '<button type="button" aria-label="' + esc(str(L.goto, 'Go to reel') + ' ' + (i + 1)) + '" data-go="' + i + '"><i></i></button>'; }).join('') + '</div>' +
          '<div class="zlr-v__media" data-media></div>' +
          '<button type="button" class="zlr-v__zone zlr-v__zone--prev" aria-label="' + esc(str(L.prev, 'Previous reel')) + '" data-prev></button>' +
          '<button type="button" class="zlr-v__zone zlr-v__zone--next" aria-label="' + esc(str(L.next, 'Next reel')) + '" data-next></button>' +
          '<div class="zlr-v__top"><button type="button" class="zlr-v__btn" data-mute aria-label="' + esc(str(L.unmute, 'Unmute')) + '"></button><span class="zlr-v__count" data-count></span><button type="button" class="zlr-v__btn" data-close aria-label="' + esc(str(L.close, 'Close')) + '">' + ICON_CLOSE + '</button></div>' +
          '<div class="zlr-v__shop" data-shop></div>' +
        '</div>' +
        '<button type="button" class="zlr-v__arrow zlr-v__arrow--prev" aria-label="' + esc(str(L.prev, 'Previous reel')) + '" data-prev>' + ICON_PREV + '</button>' +
        '<button type="button" class="zlr-v__arrow zlr-v__arrow--next" aria-label="' + esc(str(L.next, 'Next reel')) + '" data-next>' + ICON_NEXT + '</button>';
      doc.body.appendChild(v);
      this.viewer = v;
      this.media = v.querySelector('[data-media]');
      this.bars = v.querySelectorAll('.zlr-v__bars i');
      this.muteBtn = v.querySelector('[data-mute]');
      this.shop = v.querySelector('[data-shop]');
      this.stage = v.querySelector('[data-stage]');
      this.countEl = v.querySelector('[data-count]');
      if (str(L.paused)) this.countEl.setAttribute('data-paused', ' · ' + L.paused);
      this.closeBtn = v.querySelector('[data-close]');
      this.cur = 0;
      this.timer = null;
      this.held = false;
      this.stillStart = 0;
      this.stillDone = 0;
      this.busy = false;

      var self = this;
      this._onViewerClick = function (event) { self.onViewerClick(event); };
      this._onTouchStart = function (event) { self.onTouchStart(event); };
      this._onTouchEnd = function (event) { self.onTouchEnd(event); };
      v.addEventListener('click', this._onViewerClick);
      this.stage.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.stage.addEventListener('touchend', this._onTouchEnd, { passive: false });
    }

    removeViewer() {
      if (!this.viewer) return;
      clearInterval(this.timer);
      clearTimeout(this.holdTimer);
      this.viewer.removeEventListener('click', this._onViewerClick);
      this.stage.removeEventListener('touchstart', this._onTouchStart, { passive: true });
      this.stage.removeEventListener('touchend', this._onTouchEnd, { passive: false });
      if (this.viewer.parentNode) this.viewer.parentNode.removeChild(this.viewer);
      this.viewer = null;
    }

    vid() { return this.media ? this.media.querySelector('video') : null; }

    setMute() {
      var L = this.labels;
      this.muteBtn.innerHTML = this.muted ? SPK_OFF : SPK_ON;
      this.muteBtn.setAttribute('aria-label', this.muted ? str(L.unmute, 'Unmute') : str(L.mute, 'Mute'));
      var el = this.vid();
      if (el) el.muted = this.muted;
    }

    /* size picker / add to bag: keep the current reel on screen (video loops, no auto-advance) */
    pauseAuto() {
      this.held = true;
      clearInterval(this.timer);
      var el = this.vid();
      if (el) el.loop = true;
      this.viewer.classList.add('is-held');
    }

    show(i) {
      var self = this;
      var n = this.items.length;
      /* no loop: past the last reel (or before the first) nothing happens; the last reel stays on screen */
      if (this.viewerOpen && (i < 0 || i >= n)) { if (i >= n) this.showMore(); return; }
      if (this.moreEl) { this.moreEl.remove(); this.moreEl = null; }
      clearInterval(this.timer);
      this.held = false;
      this.busy = false;
      this.viewer.classList.remove('is-held');
      this.cur = ((i % n) + n) % n;
      var cur = this.cur;
      var it = this.items[cur];
      this.bars.forEach(function (b, k) { b.style.width = k < cur ? '100%' : '0'; });
      this.countEl.textContent = (cur + 1) + ' / ' + n;
      this.media.innerHTML = '';
      var src = it.videoHigh || it.video;
      var bar = this.bars[cur];
      var still = function () { self.stillDone = 0; self.runStill(); };
      if (src) {
        var video = doc.createElement('video');
        video.setAttribute('playsinline', '');
        video.setAttribute('autoplay', '');
        if (this.muted) { video.setAttribute('muted', ''); video.muted = true; }
        if (it.poster) video.setAttribute('poster', it.poster);
        if (it.alt) video.setAttribute('aria-label', it.alt);
        video.src = src;
        video.addEventListener('timeupdate', function () { if (video.duration) bar.style.width = (video.currentTime / video.duration * 100) + '%'; });
        video.addEventListener('ended', function () { if (!self.held && self.viewerOpen && self.vid() === video) self.show(self.cur + 1); });
        video.addEventListener('error', function () {
          if (self.vid() !== video) return;
          self.media.innerHTML = '';
          self.media.appendChild(self.posterImg(it, false));
          self.muteBtn.style.visibility = 'hidden';
          still();
        });
        this.media.appendChild(video);
        this.muteBtn.style.visibility = 'visible';
        /* sound on, unless the browser refuses an unmuted start (e.g. iOS for the next reel): then play muted */
        if (!this.muted) {
          var pp = video.play();
          if (pp && pp.catch) pp.catch(function () { if (self.vid() !== video) return; video.muted = true; self.muted = true; self.setMute(); var q = video.play(); if (q && q.catch) q.catch(function () {}); });
        }
      } else {
        this.media.appendChild(this.posterImg(it, true));
        this.muteBtn.style.visibility = 'hidden';
      }
      this.setMute();
      if (!src) still();
      this.renderShop('idle');
    }

    /* end card after the last reel: "watch more" appends the reels of other products (data.more) */
    showMore() {
      var lv = this.vid();
      if (lv) { try { lv.pause(); } catch (e) {} }
      clearInterval(this.timer);
      if (this.moreEl) return;
      var more = this.data.more || [];
      if (this.moreAdded || !more.length) { this.showEnd(); return; }
      var el = doc.createElement('div');
      el.className = 'zlr-v__more';
      el.innerHTML = '<p>' + esc(str(this.data.moreText)) + '</p><button type="button" data-more>' + esc(str(this.data.moreLabel, 'Watch more')) + ' ' + ICON_NEXT + '</button><button type="button" data-more-close class="zlr-v__more-x">' + esc(str(this.labels.close, 'Close')) + '</button>';
      this.stage.appendChild(el);
      this.moreEl = el;
    }

    /* the very end: the pieces from the reels, to shop straight away (instead of closing the player) */
    showEnd() {
      var seen = {};
      var cards = [];
      this.items.forEach(function (it) {
        var p = it.product;
        if (!p || !p.url || seen[p.url] || cards.length >= 6) return;
        seen[p.url] = true;
        cards.push('<a class="zlr-v__end-p" href="' + esc(p.url) + '">' + (p.image ? '<img src="' + esc(String(p.image).replace(/([?&]width=)\d+/, '$1360')) + '" alt="" loading="lazy">' : '<i></i>') +
          '<span>' + esc(p.title) + '</span><b>' + (p.on && p.wasText ? '<s>' + p.wasText + '</s> ' : '') + (p.nowText || '') + '</b></a>');
      });
      if (!cards.length) return;
      var el = doc.createElement('div');
      el.className = 'zlr-v__more zlr-v__end';
      el.innerHTML = '<p class="zlr-v__end-h">' + esc(str(this.data.endLabel, 'Shop what you watched')) + '</p>' +
        '<div class="zlr-v__end-grid">' + cards.join('') + '</div>' +
        '<div class="zlr-v__end-act"><button type="button" data-replay>' + esc(str(this.data.replayLabel, 'Watch again')) + '</button>' +
        '<button type="button" data-more-close class="zlr-v__more-x">' + esc(str(this.labels.close, 'Close')) + '</button></div>';
      this.stage.appendChild(el);
      this.moreEl = el;
    }

    loadMore() {
      if (this.moreAdded) return;
      this.moreAdded = true;
      var n = this.items.length;
      this.items = this.items.concat(this.data.more || []);
      var L = this.labels;
      var wrap = this.viewer.querySelector('.zlr-v__bars');
      wrap.innerHTML = this.items.map(function (_, k) { return '<button type="button" aria-label="' + esc(str(L.goto, 'Go to reel') + ' ' + (k + 1)) + '" data-go="' + k + '"><i></i></button>'; }).join('');
      this.bars = wrap.querySelectorAll('i');
      this.show(n);
    }

    posterImg(it, large) {
      var img = doc.createElement('img');
      var url = (large && it.posterLarge) || it.poster || '';
      if (url) img.src = url;
      img.alt = it.alt || '';
      return img;
    }

    runStill() {
      var self = this;
      clearInterval(this.timer);
      this.stillStart = Date.now() - this.stillDone;
      this.timer = setInterval(function () {
        self.stillDone = Date.now() - self.stillStart;
        var p = self.stillDone / self.stillMs;
        if (self.bars[self.cur]) self.bars[self.cur].style.width = Math.min(p, 1) * 100 + '%';
        if (p >= 1) { clearInterval(self.timer); self.show(self.cur + 1); }
      }, TICK_MS);
    }

    /* ---- shoppable product panel: state idle | pick | added (+ error message) ---- */
    renderShop(state, extra, error) {
      var it = this.items[this.cur];
      var p = it && it.product;
      var shop = this.shop;
      if (!p || !this.showShop) { shop.innerHTML = ''; shop.hidden = true; shop.removeAttribute('data-state'); return; }
      shop.hidden = false;
      var L = this.labels;
      var showPct = this.data.showDiscount !== false && p.on && p.pct > 0;
      /* nowText / wasText are store-formatted money strings rendered by Liquid (entities decode on insertion) */
      var priceH = (p.on && p.wasText ? '<s>' + p.wasText + '</s>' : '') + (p.nowText || '') + (showPct ? '<em>−' + Number(p.pct) + '%</em>' : '');
      var url = esc(p.url || '#');
      var head = '<div class="zlr-s__row"><a class="zlr-s__img" href="' + url + '">' + (p.image ? '<img src="' + esc(p.image) + '" alt="" width="120" height="160">' : '') + '</a>' +
        '<a class="zlr-s__info" href="' + url + '"><span class="zlr-s__t">' + esc(p.title) + '</span><span class="zlr-s__p">' + priceH + '</span></a>';
      var variants = p.variants || [];
      var html;
      var errorH = error ? '<div class="zlr-s__added zlr-s__added--error" role="alert"><span>' + esc(error) + '</span></div>' : '';
      if (state === 'added') {
        var what = '';
        if (extra) what = ' · <b>' + esc(p.multiOption ? extra : (str(L.sizePrefix, str(p.optionName)) ? str(L.sizePrefix, str(p.optionName)) + ' ' : '') + extra) + '</b>';
        html = '<div class="zlr-s__added" role="status">' + ICON_CHECK + '<span>' + esc(str(L.added, ZL.strings && ZL.strings.added)) + what + '</span></div>' +
          head + '<button type="button" class="zlr-s__btn" data-viewbag>' + esc(str(L.viewBag, 'View bag')) + ' (' + Number(this.bagCount || 0) + ')</button></div>';
      } else if (state === 'pick') {
        var option = p.multiOption ? (p.optionNames || [p.optionName]).join(' / ') : str(p.optionName);
        var choose = str(L.choose, 'Choose your [option]').replace(/\[option\]/g, option.toLowerCase());
        html = errorH + '<div class="zlr-s__pick"><div class="zlr-s__pick-h"><span>' + esc(choose) + '</span><button type="button" data-cancel aria-label="' + esc(str(L.cancel, 'Cancel')) + '">✕</button></div>' +
          '<div class="zlr-s__sizes' + (p.multiOption ? ' zlr-s__sizes--wide' : '') + '">' +
          variants.map(function (x) {
            return '<button type="button" data-vid="' + esc(x.id) + '" data-size="' + esc(x.title) + '"' + (x.available ? '' : ' disabled aria-label="' + esc(x.title + ' ' + str(L.variantSoldOut, 'sold out')) + '"') + '>' + esc(x.title) + '</button>';
          }).join('') +
          '</div></div>' + head + '<a class="zlr-s__link" href="' + url + '">' + esc(str(L.details, 'Details')) + '</a></div>';
      } else {
        var stock = p.available !== false && variants.some(function (x) { return x.available; });
        var first = variants.filter(function (x) { return x.available; })[0] || variants[0];
        html = errorH + head + (stock && first
          ? '<button type="button" class="zlr-s__btn" ' + (p.multi ? 'data-pick' : 'data-vid="' + esc(first.id) + '"') + '>' + esc(str(L.add, ZL.strings && ZL.strings.addToCart)) + '</button>'
          : '<a class="zlr-s__btn zlr-s__btn--ghost" href="' + url + '">' + esc(str(L.soldOut, ZL.strings && ZL.strings.soldOut)) + '</a>') + '</div>';
      }
      shop.innerHTML = html;
      shop.setAttribute('data-state', state);
    }

    onShopClick(b) {
      var self = this;
      if (this.busy) return;
      if (b.hasAttribute('data-pick')) {
        this.pauseAuto();
        this.renderShop('pick');
        var f = this.shop.querySelector('.zlr-s__sizes button:not([disabled])');
        if (f) f.focus();
        return;
      }
      if (b.hasAttribute('data-cancel')) { this.renderShop('idle'); return; }
      if (b.hasAttribute('data-viewbag')) {
        this.close(true);
        if (ZL.cart && typeof ZL.cart.open === 'function') ZL.cart.open({ keepAdded: true });
        else if (ZL.routes && ZL.routes.cart) win.location.href = ZL.routes.cart;
        return;
      }
      var vid = b.getAttribute('data-vid');
      if (!vid) return;
      var size = b.getAttribute('data-size') || '';
      var index = this.cur;
      var product = this.items[index].product;
      this.pauseAuto();
      this.busy = true;
      b.setAttribute('aria-busy', 'true');
      this.shop.setAttribute('aria-busy', 'true');
      this.addToCart(Number(vid)).then(function (res) {
        self.busy = false;
        if (!self.viewer) return;
        self.shop.removeAttribute('aria-busy');
        b.removeAttribute('aria-busy');
        if (!self.viewerOpen || self.cur !== index) return;   /* the shopper moved on meanwhile */
        if (res && res.ok) {
          var count = res.cart && res.cart.item_count != null ? res.cart.item_count : null;
          if (count == null && ZL.cart && typeof ZL.cart.count === 'function') count = ZL.cart.count();
          self.bagCount = count == null ? 1 : count;
          self.renderShop('added', size);
          var viewBag = self.shop.querySelector('[data-viewbag]');
          if (viewBag) viewBag.focus({ preventScroll: true });
        } else {
          var msg = (res && res.error) || str(self.labels.addError, ZL.strings && ZL.strings.error);
          self.renderShop(product && product.multi ? 'pick' : 'idle', null, typeof msg === 'string' ? msg : str(self.labels.addError));
        }
      });
    }

    /* never rejects: { ok, cart, error } like ZL.cart.add */
    addToCart(id) {
      var items = [{ id: id, quantity: 1 }];
      if (ZL.cart && typeof ZL.cart.add === 'function') {
        try {
          return Promise.resolve(ZL.cart.add(items, { open: false })).then(function (res) {
            return res || { ok: false };
          }, function (err) {
            return { ok: false, error: err && err.message };
          });
        } catch (e) {
          return Promise.resolve({ ok: false, error: e && e.message });
        }
      }
      /* no cart API on the page (should not happen): plain form post to /cart/add */
      var form = doc.createElement('form');
      form.method = 'post';
      form.action = (ZL.routes && ZL.routes.cartAdd) || '/cart/add';
      form.hidden = true;
      form.innerHTML = '<input type="hidden" name="id" value="' + esc(id) + '"><input type="hidden" name="quantity" value="1">';
      doc.body.appendChild(form);
      form.submit();
      return new Promise(function () {});
    }

    /* ---- open / close / navigation ---- */
    open(i) {
      if (!this._ready) return;
      this.buildViewer();
      this.last = doc.activeElement;
      this.viewerOpen = true;
      this.viewer.classList.add('is-on');
      this.viewer.setAttribute('aria-hidden', 'false');
      if (typeof ZL.lockScroll === 'function') ZL.lockScroll(this.lockId);
      else doc.documentElement.style.overflow = 'hidden';
      this.root.querySelectorAll('video').forEach(function (x) { x.pause(); });
      this.muted = false;
      this.show(i);
      doc.addEventListener('keydown', this._onKey);
      if (typeof ZL.trapFocus === 'function') ZL.trapFocus(this.viewer, { initialFocus: this.closeBtn, returnFocus: this.last });
      else this.closeBtn.focus({ preventScroll: true });
      if (typeof ZL.emit === 'function') ZL.emit('zl:reels:open', { sectionId: this.sectionId, index: this.cur });
    }

    close(toBag) {
      if (!this.viewerOpen || !this.viewer) return;
      this.viewerOpen = false;
      clearInterval(this.timer);
      clearTimeout(this.holdTimer);
      this.viewer.classList.remove('is-on', 'is-holding', 'is-held');
      this.viewer.setAttribute('aria-hidden', 'true');
      this.media.innerHTML = '';
      if (this.moreEl) { this.moreEl.remove(); this.moreEl = null; }
      doc.removeEventListener('keydown', this._onKey);
      if (typeof ZL.unlockScroll === 'function') ZL.unlockScroll(this.lockId);
      else doc.documentElement.style.overflow = '';
      if (typeof ZL.releaseFocus === 'function') ZL.releaseFocus(this.viewer, { restore: false });
      if (!toBag && this.last && this.last.focus && doc.contains(this.last)) {
        try { this.last.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      }
      if (typeof ZL.emit === 'function') ZL.emit('zl:reels:close', { sectionId: this.sectionId, toBag: Boolean(toBag) });
    }

    onKey(event) {
      if (!this.viewerOpen) return;
      if (event.key === 'Escape' || event.key === 'Esc') this.close();
      else if (event.key === 'ArrowRight') this.show(this.cur + 1);
      else if (event.key === 'ArrowLeft') this.show(this.cur - 1);
    }

    onViewerClick(event) {
      var v = this.viewer;
      if (event.target === v) { this.close(); return; }   /* click outside the video (desktop) */
      var b = event.target.closest('button, a');
      if (!b || !v.contains(b)) return;
      if (this.shop.contains(b)) { if (b.tagName === 'BUTTON') this.onShopClick(b); return; }
      if (b.hasAttribute('data-close') || b.hasAttribute('data-more-close')) { this.close(); return; }
      if (b.hasAttribute('data-more')) { this.loadMore(); return; }
      if (b.hasAttribute('data-replay')) { this.show(0); return; }
      if (b.hasAttribute('data-mute')) { this.muted = !this.muted; this.setMute(); return; }
      if (b.hasAttribute('data-go')) { this.show(Number(b.getAttribute('data-go'))); return; }
      if (b.hasAttribute('data-prev')) { this.show(this.cur - 1); return; }
      if (b.hasAttribute('data-next')) this.show(this.cur + 1);
    }

    /* ---- touch: swipe left/right = next/prev, swipe down = close, press & hold = pause ---- */
    onTouchStart(event) {
      if (event.target.closest('[data-shop],.zlr-v__top,.zlr-v__bars')) return;
      var self = this;
      var t = event.touches[0];
      this.sx = t.clientX;
      this.sy = t.clientY;
      this.wasHeld = false;
      clearTimeout(this.holdTimer);
      this.holdTimer = setTimeout(function () {
        self.wasHeld = true;
        var el = self.vid();
        if (el) el.pause(); else clearInterval(self.timer);
        self.viewer.classList.add('is-holding');
      }, HOLD_MS);
    }

    onTouchEnd(event) {
      clearTimeout(this.holdTimer);
      if (event.target.closest('[data-shop],.zlr-v__top,.zlr-v__bars')) return;
      var t = event.changedTouches[0];
      var dx = t.clientX - this.sx;
      var dy = t.clientY - this.sy;
      if (this.wasHeld) {
        this.wasHeld = false;
        this.viewer.classList.remove('is-holding');
        if (!this.held) { var el = this.vid(); if (el) playSafe(el); else this.runStill(); }
        event.preventDefault();
        return;
      }
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) { event.preventDefault(); this.show(this.cur + (dx < 0 ? 1 : -1)); }
      else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) { event.preventDefault(); this.close(); }
    }
  }

  if (!customElements.get('zl-reels')) customElements.define('zl-reels', ZlReels);

  /* ---------------------------------------------------------------- public API */
  var api = {
    /* (re)place every reels strip: into the first free slot, else in place */
    mount: function () { instances.forEach(function (inst) { inst.mount(); }); },
    /* put a strip into this slot element (first instance whose placement is "slot", else the first one) */
    mountInto: function (slotEl) {
      if (!slotEl) return;
      var pick = null;
      for (var i = 0; i < instances.length; i++) {
        if (instances[i].placement === 'slot' && (!instances[i].currentSlot || !instances[i].currentSlot.isConnected || instances[i].currentSlot === slotEl)) { pick = instances[i]; break; }
      }
      (pick || instances[0]) && (pick || instances[0]).mountInto(slotEl);
    },
    /* close the viewers and take the strips off the page (mount() restores them) */
    destroy: function () { instances.forEach(function (inst) { inst.unmount(); }); },
    instances: function () { return instances.slice(); }
  };
  ZL.reels = Object.assign(ZL.reels || {}, api);
  if (!win.ZLReels) win.ZLReels = ZL.reels;
})();
